import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { FC, useMemo } from "react";

import type { ILlmUsageEvent } from "@monorepo/core";

export type LlmUsageMetricKey =
  | "eventCount"
  | "inputTokenCount"
  | "outputTokenCount"
  | "totalTokenCount"
  | "estimatedCostMicros";

const METRIC_DEFINITIONS: Record<
  LlmUsageMetricKey,
  {
    label: string;
    colorIndex: number;
    getValue: (event: ILlmUsageEvent) => number;
    formatValue: (value: number) => string;
  }
> = {
  eventCount: {
    label: "Event Count",
    colorIndex: 0,
    getValue: () => 1,
    formatValue: (value) => value.toLocaleString(),
  },
  inputTokenCount: {
    label: "Input Tokens",
    colorIndex: 1,
    getValue: (event) => event.inputTokenCount ?? 0,
    formatValue: (value) => Math.round(value).toLocaleString(),
  },
  outputTokenCount: {
    label: "Output Tokens",
    colorIndex: 2,
    getValue: (event) => event.outputTokenCount ?? 0,
    formatValue: (value) => Math.round(value).toLocaleString(),
  },
  totalTokenCount: {
    label: "Total Tokens",
    colorIndex: 3,
    getValue: (event) => event.totalTokenCount ?? 0,
    formatValue: (value) => Math.round(value).toLocaleString(),
  },
  estimatedCostMicros: {
    label: "Estimated Cost",
    colorIndex: 4,
    getValue: (event) => event.estimatedCostMicros ?? 0,
    formatValue: (value) => `$${(value / 1_000_000).toFixed(4)}`,
  },
};

const CHART_HEIGHT = 180;
const CHART_WIDTH = 720;
const PADDING = {
  top: 18,
  right: 18,
  bottom: 28,
  left: 18,
};

const formatBucketLabel = (timestamp: string, bucket: "hour" | "day") => {
  const date = new Date(timestamp);
  return bucket === "hour"
    ? date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
      })
    : date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
};

const toBucketKey = (timestamp: string, bucket: "hour" | "day") => {
  const date = new Date(timestamp);
  if (bucket === "hour") {
    date.setMinutes(0, 0, 0);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

const inferBucketSize = (events: readonly ILlmUsageEvent[]) => {
  if (events.length <= 1) {
    return "hour" as const;
  }

  const timestamps = events.map((event) => new Date(event.createdAt).getTime());
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const rangeMs = maxTime - minTime;
  const twoDaysMs = 2 * 24 * 60 * 60 * 1000;

  return rangeMs <= twoDaysMs ? ("hour" as const) : ("day" as const);
};

const buildLinePath = ({
  points,
  maxValue,
}: {
  points: readonly number[];
  maxValue: number;
}) => {
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const divisor = Math.max(points.length - 1, 1);

  return points
    .map((value, index) => {
      const x = PADDING.left + (innerWidth * index) / divisor;
      const y =
        PADDING.top +
        innerHeight -
        (Math.max(value, 0) / Math.max(maxValue, 1)) * innerHeight;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
};

const getPointX = (index: number, length: number) => {
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const divisor = Math.max(length - 1, 1);
  return PADDING.left + (innerWidth * index) / divisor;
};

const getPointY = (value: number, maxValue: number) => {
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  return (
    PADDING.top +
    innerHeight -
    (Math.max(value, 0) / Math.max(maxValue, 1)) * innerHeight
  );
};

interface MetricPanelProps {
  metric: LlmUsageMetricKey;
  labels: readonly string[];
  values: readonly number[];
}

const MetricPanel: FC<MetricPanelProps> = ({ metric, labels, values }) => {
  const theme = useTheme();
  const definition = METRIC_DEFINITIONS[metric];
  const paletteColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
  ];
  const color =
    paletteColors[definition.colorIndex] ?? theme.palette.primary.main;
  const maxValue = Math.max(...values, 1);
  const latestValue = values.at(-1) ?? 0;
  const linePath = buildLinePath({ points: values, maxValue });

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.7),
        background: `linear-gradient(180deg, ${alpha(color, 0.14)}, ${alpha(
          theme.palette.background.paper,
          0.92,
        )})`,
      })}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            {definition.label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {definition.formatValue(latestValue)}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Max {definition.formatValue(maxValue)}
        </Typography>
      </Stack>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          width="100%"
          height={CHART_HEIGHT}
          role="img"
          aria-label={`${definition.label} chart`}
        >
          <line
            x1={PADDING.left}
            y1={CHART_HEIGHT - PADDING.bottom}
            x2={CHART_WIDTH - PADDING.right}
            y2={CHART_HEIGHT - PADDING.bottom}
            stroke={alpha(theme.palette.divider, 0.7)}
            strokeWidth="1"
          />
          <line
            x1={PADDING.left}
            y1={PADDING.top}
            x2={PADDING.left}
            y2={CHART_HEIGHT - PADDING.bottom}
            stroke={alpha(theme.palette.divider, 0.35)}
            strokeWidth="1"
          />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {values.map((value, index) => (
            <circle
              key={`${metric}-${labels[index] ?? index}`}
              cx={getPointX(index, values.length)}
              cy={getPointY(value, maxValue)}
              r="3.5"
              fill={color}
              stroke={theme.palette.background.paper}
              strokeWidth="1.5"
            />
          ))}
          {labels.length > 0 ? (
            <>
              <text
                x={PADDING.left}
                y={CHART_HEIGHT - 8}
                fill={theme.palette.text.secondary}
                fontSize="11"
              >
                {labels[0]}
              </text>
              <text
                x={CHART_WIDTH - PADDING.right}
                y={CHART_HEIGHT - 8}
                textAnchor="end"
                fill={theme.palette.text.secondary}
                fontSize="11"
              >
                {labels.at(-1)}
              </text>
            </>
          ) : null}
        </svg>
      </Box>
    </Box>
  );
};

const EmptyChartState: FC = () => (
  <Box
    sx={(theme) => ({
      p: 4,
      borderRadius: 3,
      border: "1px dashed",
      borderColor: alpha(theme.palette.divider, 0.8),
      textAlign: "center",
      color: "text.secondary",
    })}
  >
    <Typography variant="body1">
      No usage events match the current filters.
    </Typography>
  </Box>
);

export const LLM_USAGE_METRIC_OPTIONS = Object.entries(METRIC_DEFINITIONS).map(
  ([value, definition]) => ({
    value: value as LlmUsageMetricKey,
    label: definition.label,
  }),
);

export const LlmUsageChart: FC<{
  events: readonly ILlmUsageEvent[];
  metrics: readonly LlmUsageMetricKey[];
}> = ({ events, metrics }) => {
  const grouped = useMemo(() => {
    if (events.length === 0) {
      return {
        labels: [] as string[],
        series: {} as Record<LlmUsageMetricKey, number[]>,
      };
    }

    const bucket = inferBucketSize(events);
    const bucketMap = new Map<string, ILlmUsageEvent[]>();
    [...events]
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
      .forEach((event) => {
        const key = toBucketKey(event.createdAt, bucket);
        const currentBucket = bucketMap.get(key) ?? [];
        currentBucket.push(event);
        bucketMap.set(key, currentBucket);
      });

    const bucketEntries = Array.from(bucketMap.entries()).sort((left, right) =>
      left[0].localeCompare(right[0]),
    );
    const labels = bucketEntries.map(([timestamp]) =>
      formatBucketLabel(timestamp, bucket),
    );
    const series = Object.keys(METRIC_DEFINITIONS).reduce(
      (accumulator, metric) => {
        accumulator[metric as LlmUsageMetricKey] = bucketEntries.map(
          ([, bucketEvents]) =>
            bucketEvents.reduce(
              (total, event) =>
                total +
                METRIC_DEFINITIONS[metric as LlmUsageMetricKey].getValue(event),
              0,
            ),
        );
        return accumulator;
      },
      {} as Record<LlmUsageMetricKey, number[]>,
    );

    return { labels, series };
  }, [events]);

  if (events.length === 0 || metrics.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <Stack spacing={2.5}>
      {metrics.map((metric) => (
        <MetricPanel
          key={metric}
          metric={metric}
          labels={grouped.labels}
          values={grouped.series[metric] ?? []}
        />
      ))}
    </Stack>
  );
};

export default LlmUsageChart;
